import * as React from 'react';
import propz from 'propz';
import { Autocomplete } from '../Autocomplete/Autocomplete';
import { FunctionComponent } from 'react';
import moment from 'moment';

interface Props {
  request: any;
  isUserAlreadyLinkedError: boolean;
  onSelectForm: (form: any) => void;
  onSelectHouse: (house: any) => void;
  onSelectStudent: (student: any) => void;
  onAcceptPermission: () => void;
  onClearPermission: () => void;
  searchFunctionForms: (text: string) => void;
  searchFunctionHouses: (text: string) => void;
  searchFunctionStudents: (text: string) => void;
  getFormOrHouseTitle: (item: any) => string;
  getStudentTitle: (item: any) => string;
  onChangeToStudent: (event: any, student: any) => void;
  form: any;
  house: any;
  student: any;
  students: any[];
}

export const RequestAcceptParent: FunctionComponent<Props> = props => {
  const {
    request,
    isUserAlreadyLinkedError,
    onSelectForm,
    onSelectHouse,
    onAcceptPermission,
    onClearPermission,
    onSelectStudent,
    searchFunctionForms,
    searchFunctionHouses,
    searchFunctionStudents,
    getFormOrHouseTitle,
    getStudentTitle,
    form,
    house,
    student,
    students
  } = props;

  const comment = propz.get(request, ['requestedPermission', 'comment']);
  const requesterFirstName = propz.get(request, ['requester', 'firstName'], '');
  const requesterLastName = propz.get(request, ['requester', 'lastName'], '');

  const getChildDetails = () => {
    const childDetails = [];

    if (typeof request.requestedPermission.childDateOfBirth !== 'undefined') {
      childDetails.push({ label: 'DOB', value: moment(request.requestedPermission.childDateOfBirth).format('DD MMM YYYY') });
    }

    if (typeof request.requestedPermission.childGender !== 'undefined') {
      childDetails.push({ label: 'Gender', value: request.requestedPermission.childGender });
    }

    if (typeof request.requestedPermission.form !== 'undefined') {
      const formName = propz.get(request, ['requestedPermission', 'form', 'name'], '');
      childDetails.push({ label: 'Form', value: formName });
    }

    if (typeof request.requestedPermission.house !== 'undefined') {
      const houseName = propz.get(request, ['requestedPermission', 'house', 'name'], '');
      childDetails.push({ label: 'House', value: houseName });
    }

    return childDetails;
  };

  const renderChildDetails = () => {
    const childDetails = getChildDetails();

    if (childDetails.length === 0) {
      return null;
    }

    return (
      <div className="requestAcceptParent__details">
        {childDetails.map(item => (
          <div key={item.label}>
            <span>{item.label}</span>
            <strong>{item.value}</strong>
          </div>
        ))}
      </div>
    );
  };

  const renderStudentList = () => {
    if (students.length === 0) {
      return null;
    }

    return (
      <div className="requestAcceptParent__suggestions">
        <div className="requestAcceptParent__sectionTitle">Suggested students</div>
        <div className="requestAcceptParent__studentGrid">
          {students.map((studentItem, index) => {
            const studentFormName = propz.get(studentItem, ['form', 'name'], '');
            const studentHouseName = propz.get(studentItem, ['house', 'name'], '');
            const initials = `${(studentItem.firstName || '?').charAt(0)}${(studentItem.lastName || '').charAt(0)}`;
            const isSelected = typeof student !== 'undefined' && student.id === studentItem.id;

            return (
              <a
                key={`student_${index}`}
                className={isSelected ? 'requestAcceptParent__student requestAcceptParent__student--selected' : 'requestAcceptParent__student'}
                href=""
                onClick={event => props.onChangeToStudent(event, studentItem)}
              >
                <span className="requests-avatar"><span>{initials}</span></span>
                <span>
                  <strong>{studentItem.firstName} {studentItem.lastName}</strong>
                  <small>{[studentFormName, studentHouseName].filter(Boolean).join(' · ') || 'No form or house'}</small>
                </span>
              </a>
            );
          })}
        </div>
      </div>
    );
  };

  const isExistStudent = typeof student !== 'undefined';

  return (
    <div className="requestAcceptParent">
      <div className="requestAcceptParent__card">
        <div className="requestAcceptParent__head">
          <div className="requestActionModal__icon">P</div>
          <div>
            <h1>Accept parent role</h1>
            <p>Link {requesterFirstName} {requesterLastName} to the correct child before accepting this request.</p>
          </div>
        </div>

        {typeof comment !== 'undefined' && comment !== '' && (
          <div className="requestAcceptParent__message">
            <span>Parent message</span>
            <strong>{comment}</strong>
          </div>
        )}

        {renderChildDetails()}

        <div className="requestAcceptParent__sectionTitle">Find child</div>
        <div className="requestAcceptParent__grid">
          <label className="requestMergeForm__field">
            <span>Form</span>
            <Autocomplete
              searchFunction={searchFunctionForms}
              getElementTitle={getFormOrHouseTitle}
              onSelect={onSelectForm}
              defaultItem={form}
              customClass="requestMergeForm__autocomplete"
            />
          </label>
          <label className="requestMergeForm__field">
            <span>House</span>
            <Autocomplete
              searchFunction={searchFunctionHouses}
              getElementTitle={getFormOrHouseTitle}
              onSelect={onSelectHouse}
              defaultItem={house}
              customClass="requestMergeForm__autocomplete"
            />
          </label>
          <label className="requestMergeForm__field">
            <span>Student name</span>
            <Autocomplete
              searchFunction={searchFunctionStudents}
              getElementTitle={getStudentTitle}
              onSelect={onSelectStudent}
              defaultItem={student}
              customClass="requestMergeForm__autocomplete"
            />
          </label>
        </div>

        {isUserAlreadyLinkedError && (
          <div className="requestAcceptParent__error">The user has already been linked to the selected child.</div>
        )}

        {renderStudentList()}

        <div className="requestAcceptParent__foot">
          <button type="button" className="people-btn people-btn--secondary" onClick={onClearPermission} disabled={!isExistStudent}>
            Clear selection
          </button>
          <button type="button" className="people-btn people-btn--primary" onClick={onAcceptPermission} disabled={!isExistStudent}>
            Accept parent role
          </button>
        </div>
      </div>
    </div>
  );
};

RequestAcceptParent.displayName = 'RequestAcceptParent';
